import React, { useState } from 'react';
import Popup from 'components/Popup';
import InternalForm from 'components/InternalForm';
import Table from 'components/Table';
import {
  BadgeCheck, Pencil, Plus, Trash,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { formatDate, getStartupVerifiedIcon } from 'utils/functions';
import { useCounter } from 'utils/customHooks';
import PropTypes from 'prop-types';
import PopupVerify from 'components/PopupVerify';
import { verifyInvestmentAsset } from 'modules/investmentAssets/repository';
import InputSelect from 'components/Inputs/inputSelect';
import useHandleAssets from '../hooks/useHandleAssets';
import PopupMassive from './PopupMassive';

function AdminInvestmentAsset({ type }) {
  const literals = useSelector((state) => state.i18n.literals.investmentAssets);
  const literalsCommon = useSelector((state) => state.i18n.literals.common);
  const [editAsset, setEditAsset] = useState(null);
  const [verifyAsset, setVerifyAsset] = useState(null);
  const [popupMassive, setPopupMassive] = useState(null);
  const tableRefreshFlag = useCounter();

  const {
    getInvestments,
    createInvestment,
    deleteInvestment,
  } = useHandleAssets({
    type,
    refresh: tableRefreshFlag.increase,
  });

  const columns = [
    {
      field: 'logo',
      type: 'avatar',
      title: (v, row) => row.name,
    },
    {
      field: 'name',
      label: literalsCommon.name,
      preRender: (name, row) => (
        <div className='d-flex align-items-center'>
          {name}
          {getStartupVerifiedIcon(row.verified)}
        </div>
      ),
    },
    {
      field: 'createdAt',
      width: '180px',
      label: literalsCommon.createdAt,
      preRender: date => formatDate(date, { time: true }),
    },
    {
      field: 'info',
      type: 'menu',
      buttons: [
        {
          icon: Pencil,
          text: literalsCommon.edit,
          onClick: (row) => setEditAsset(row),
        },
        {
          icon: Trash,
          mode: 'danger',
          text: literalsCommon.delete,
          onClick: (row) => deleteInvestment(row.id),
        },
        {
          icon: BadgeCheck,
          text: literals.verification,
          onClick: (row) => setVerifyAsset(row.id),
        },
      ],
    },
  ];
  return (
    <>
      <Table
        key={`table_${type}`}
        columns={columns}
        pageSize={10}
        onClickRow={setEditAsset}
        fetch={getInvestments}
        forceFetch={tableRefreshFlag.value}
        actions={[{
          icon: Plus,
          text: literalsCommon.add,
          onClick: () => setEditAsset({ id: '' }),
        }, {
          icon: <Plus />,
          name: literals.createMasive,
          mode: 'primary',
          onClick: () => setPopupMassive(type),
        }]}
      />
      {editAsset && (
        <Popup title={literalsCommon.edit} onClose={() => setEditAsset(null)}>
          <div className='row'>
            <div className='col-12 col-md-6 offset-md-6'>
              <InputSelect
                preText={literals.verification}
                value={editAsset.verified}
                onChange={verified => setEditAsset({ ...editAsset, verified })}
                options={[
                  { id: 'non', name: 'Non' },
                  { id: 'team', name: 'Team' },
                  { id: 'full', name: 'Full' },
                ]}
              />
            </div>
          </div>
          <br />
          <InternalForm
            type={type}
            answers={editAsset}
            onSubmit={async (aux) => {
              const asset = { ...aux, verified: editAsset.verified };
              await createInvestment(asset, editAsset.id);
              tableRefreshFlag.increase();
              setEditAsset(null);
            }}
          />
        </Popup>
      )}
      {verifyAsset && (
        <PopupVerify
          title={literalsCommon.edit}
          onSubmit={async (v) => {
            await verifyInvestmentAsset(verifyAsset, v);
            tableRefreshFlag.increase();
          }}
          onClose={() => setVerifyAsset(null)}
          options={[
            { id: 'non', title: 'Non' },
            { id: 'team', title: 'Team' },
            { id: 'full', title: 'Full' },
          ]}
        />
      )}
      {popupMassive && (
        <PopupMassive
          onClose={() => setPopupMassive(null)}
          onSuccess={() => tableRefreshFlag.increase()}
          type={popupMassive}
        />
      )}
    </>
  );
}

AdminInvestmentAsset.propTypes = {
  type: PropTypes.string.isRequired,
};

export default AdminInvestmentAsset;
